import React, { useState } from 'react';
import { Paper, Typography, Stack, Divider, Link, useTheme, Box, Button, Tooltip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { format } from 'date-fns';
import { IconWrapper } from 'components/Icon';
import { ImageAvatar } from 'components/Avatar';
import { paths } from 'routes';
import { Modal } from 'components/Modal';
import { useRecordOutcome } from '../../api/recordResponse';

const getStatusColor = (status, lastDayToRespond, theme) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const lddr = new Date(lastDayToRespond);

  if (status === 'draft') {
    return theme.palette.warning.main;
  }

  if (status === 'withdrawn' || lddr < today) {
    return theme.palette.error.main;
  }

  if (status === 'active' && lddr >= today) {
    return theme.palette.success.main;
  }

  return theme.palette.grey[500]; // Fallback for any other unexpected status
};

const isEnded = (status, lastDayToRespond) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const lddr = new Date(lastDayToRespond);

  if (status === 'draft') return false;
  if (status === 'withdrawn' || lddr < today) return true;
  if (status === 'active' && lddr >= today) return false;
  return true;
};

export const KanbanCard = (props) => {
  const theme = useTheme();
  const { role, description, buyer_name, buyer_hidden, hide_buyer, last_day_to_respond, buyer_logo_url, buyer_slug, status, ad_id, assigned_user_name, assigned_user_image_url } = props;
  const [warningOpen, setWarningOpen] = useState(false);
  const buyer_url = buyer_slug?.length > 0 ? `${paths.public.companyPublicProfile("buyer", buyer_slug)}` : '';
  const recordOutcomeMutation = useRecordOutcome();

  const handleRecordOutcome = (outcome) => {
    recordOutcomeMutation.mutate(
      { jobAdID: ad_id, outcome },
      {
        onSuccess: () => setWarningOpen(false),
      }
    );
  };

  return (
    <>
    <div
      onClick={(event) => {
        event.stopPropagation();
      }}
      onMouseDown={(event) => {
        event.stopPropagation();
      }}
    >
    <Paper
      elevation={1}
      sx={{
        p: 2, // 16px
        border: '1px solid #e3e3e3',
        '&:hover': {
          boxShadow: 3,
        },
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          height: '50px',
          width: '3px',
          backgroundColor: getStatusColor(status, last_day_to_respond, theme),
          borderRadius: '0 4px 4px 0',
        }}
      />
      <Stack gap={1.5}>
        <Stack direction="row" gap={1} alignItems="center"> {/* 8px */}
          <Link component={RouterLink} to={buyer_url} color="inherit" underline="none">
            <ImageAvatar img={buyer_logo_url || ''} name={buyer_name || ''} anonymous={buyer_hidden} sx={{ width: 22, height: 22 }} />
          </Link>
          <Typography sx={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '16px', lineHeight: '22px', color: 'text.primary' }}>
            {role}
          </Typography>
          {hide_buyer && (
            <Tooltip title="Buyer is hidden">
              <Box display="flex">
                <IconWrapper
                  icon="hidden"
                  sx={{
                    mt: -0.4,
                    svg: { stroke: theme.palette.text.secondary, color: theme.palette.text.secondary, strokeWidth: 1.5, width: 16, height: 16 },
                  }}
                  height={16}
                />
              </Box>
            </Tooltip>
          )}
        </Stack>
        <Typography
          sx={{
            fontFamily: 'Montserrat',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '16px',
            color: '#828282',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {description}
        </Typography>
        <Divider />
        <Stack direction="row" justifyContent="space-between" alignItems="center" justifyItems="center">
          <Stack direction="row" gap={1} alignItems="center"> {/* 8px */}
            {assigned_user_image_url ? (
              <ImageAvatar img={assigned_user_image_url} name={assigned_user_name} sx={{ width: 16, height: 16 }} />
            ) : (
              <IconWrapper icon="user" sx={{mt: -0.475 , svg: {stroke: 'grey.600', color: 'grey.600', strokeWidth: 1.2, width: 16, height: 16 } }} height={16}  />
            )}
            <Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 400, lineHeight: '20px', color: 'text.primary' }}>{assigned_user_name || 'To be assigned'}</Typography>
          </Stack>
          <Stack direction="row" gap={0.5} alignItems="center"> {/* 4px */}
            {isEnded(status, last_day_to_respond) && (
              <Box
                component="span"
                role="button"
                tabIndex={0}
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setWarningOpen(true);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.stopPropagation();
                    setWarningOpen(true);
                  }
                }}
                aria-label="Ended details"
                sx={{
                  p: 0,
                  ml: 0,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconWrapper icon="warning" sx={{ mt: 0.1, svg: { stroke: '#FDB526!important', strokeWidth: 1.4, width: 24, height: 24 } }} height={24} />
              </Box>
            )}
            <IconWrapper icon="calendar" sx={{mt: -0.4, svg: {stroke: '#828282', color: '#828282', strokeWidth: 1.2, width: 16, height: 16 } }} height={16}  />
            <Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 400, lineHeight: '20px', color: '#828282' }}>
              {format(new Date(last_day_to_respond), 'dd MMM')}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
      <Modal title={role} open={warningOpen} handleClose={() => setWarningOpen(false)} sx={{ width: 640, p: 4 }}>
        <Stack gap={2} sx={{ px: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -1 }}>
            <Link
              component={RouterLink}
              to={paths.myJobAdsDetailsView(props.ad_id)}
              underline="none"
              sx={{ fontSize: 15, fontWeight: 600, color: 'primary.main' }}
            >
              Go to details view
            </Link>
          </Box>
          <Typography sx={{ fontFamily: 'Montserrat', fontSize: 22, fontWeight: 700, color: 'text.primary', textAlign: 'center', lineHeight: 1.3, mt: 0 }}>
            Did you win this assignment?
          </Typography>
          <Typography sx={{ fontFamily: 'Montserrat', fontSize: 15, fontWeight: 600, color: 'text.secondary', textAlign: 'center', lineHeight: 1.4, mt: 0.5 }}>
            By answering this question, you help us improve our services!
          </Typography>
          <Typography sx={{ fontFamily: 'Montserrat', fontSize: 14, fontWeight: 500, color: 'text.secondary', textAlign: 'center', lineHeight: 1.4 }}>
            When answered your job ad will be removed from the dashboard and can be accessed via the archive tab.
          </Typography>
          <Stack direction="row" gap={2} justifyContent="center" pt={1}>
            <Button
              variant="contained"
              disabled={recordOutcomeMutation.isPending}
              onClick={() => handleRecordOutcome('lost')}
              sx={{
                backgroundColor: '#D81B1B',
                color: '#fff',
                minWidth: 140,
                fontSize: 14,
                borderRadius: 3,
                textTransform: 'none',
                py: 1.25,
                '&:hover': { backgroundColor: '#B71C1C' },
              }}
            >
              Loss
            </Button>
            <Button
              variant="contained"
              disabled={recordOutcomeMutation.isPending}
              onClick={() => handleRecordOutcome('won')}
              sx={{
                backgroundColor: '#3BB54A',
                color: '#fff',
                minWidth: 140,
                fontSize: 14,
                borderRadius: 3,
                textTransform: 'none',
                py: 1.25,
                '&:hover': { backgroundColor: '#2E8B3A' },
              }}
            >
              Win
            </Button>
          </Stack>
        </Stack>
      </Modal>
    </div>
    </>
  );
};
